import React from "react";
import "../styles/MarketStateBadges.css";

const EXCHANGE_LABELS = {
  MANTENIMIENTO: "Cerrado en Pionex: par en mantenimiento",
  SIN_LIQUIDEZ: "Cerrado en Pionex: mercado detenido (sin volumen)",
  SIN_DATOS: "Cerrado en Pionex: sin datos del símbolo",
};

const JORNADA_LABELS = {
  BLOQUEADA: "Fuera de jornada NY (bot sin compra)",
  DESCONOCIDO: "Jornada NY indeterminada (bot sin compra)",
};

/**
 * Traduce `estado_mercado` (backend: exchange + jornada) a una lista de badges.
 *
 * Los dos ejes NUNCA se fusionan en una sola etiqueta: "el exchange tiene el par en
 * mantenimiento" y "el bot esta fuera de jornada NY" son hechos distintos con
 * consecuencias distintas (el primero te deja el capital bloqueado en el exchange; el
 * segundo es una decision del bot y el par se puede operar a mano).
 *
 * Solo se muestra lo que IMPIDE operar. La jornada se filtra por `no_compra` y no por
 * su estado: REDUCIDA es una property de la HORA, no del par, asi que salia en los 12
 * pares a la vez y no decia nada de ningun par en concreto. El bot sigue comprando
 * (con tamano reducido), asi que no es informacion de "no se puede negociar" y solo
 * genera ruido. El badge de jornada aparece unicamente cuando el bot NO compra
 * (feriado NYSE / `BLOQUEADA`).
 *
 * Devuelve datos y no JSX para poder testearlo y reutilizarlo en texto plano.
 */
export const buildMarketStateBadges = (estado) => {
  const exchange = estado?.exchange || {};
  const jornada = estado?.jornada || {};
  const items = [];

  const exchangeEstado = String(exchange.estado || "OPERATIVO").toUpperCase();
  if (exchangeEstado === "MANTENIMIENTO" || exchangeEstado === "SIN_LIQUIDEZ" || exchangeEstado === "SIN_DATOS") {
    items.push({
      key: "exchange",
      tone: exchangeEstado === "SIN_DATOS" ? "warning" : "danger",
      label: EXCHANGE_LABELS[exchangeEstado] || "Cerrado en Pionex: par no disponible",
      title: [
        exchangeEstado === "MANTENIMIENTO"
          ? "El exchange rechazo una orden para este par."
          : exchangeEstado === "SIN_LIQUIDEZ"
          ? "El exchange devuelve el precio pero con volumen 0: no hay mercado para operar."
          : "El exchange no entrega datos para calcular los indicadores de este par.",
        exchange.motivo ? `Motivo: ${exchange.motivo}` : null,
        exchange.ts_iso ? `Desde: ${exchange.ts_iso}` : null,
      ]
        .filter(Boolean)
        .join(" "),
    });
  }

  const jornadaEstado = String(jornada.estado || "").toUpperCase();
  if (jornada.no_compra) {
    items.push({
      key: "jornada",
      tone: "danger",
      label: JORNADA_LABELS[jornadaEstado] || "Fuera de jornada NY (bot sin compra)",
      title: [
        jornada.motivo || "El bot esta fuera de su ventana horaria y no compra.",
        jornada.descripcion || null,
        jornada.horas_hasta_apertura
          ? `Apertura NY en ~${jornada.horas_hasta_apertura}h.`
          : null,
      ]
        .filter(Boolean)
        .join(" "),
    });
  }

  return items;
};

/**
 * Estado POSITIVO y explicito de si ese par se puede negociar ahora.
 *
 * Pensado para las ordenes activas, donde la pregunta del usuario es concreta: "esta
 * orden, puedo venderla ya o el mercado esta?". Ahi siempre se dice algo, porque son pocas
 * filas y la respuestaabsence es informacion. En la grilla de pares NO se usa: ahi solo
 * van las alertas, porque la jornada es igual para todos los pares y repetirla 12 veces
 * no dice nada de ninguno.
 *
 * Prioriza el exchange sobre la jornada: si el exchange tiene el par en mantenimiento,
 * el horario del bot es irrelevante.
 */
export const buildEstadoMercarioTexto = (estado) => {
  const exchange = estado?.exchange || {};
  const jornada = estado?.jornada || {};

  const exchangeEstado = String(exchange.estado || "OPERATIVO").toUpperCase();
  if (exchangeEstado === "MANTENIMIENTO") {
    return {
      tone: "danger",
      texto: "No negociable: Pionex lo tiene en mantenimiento",
      detalle: exchange.motivo || "Pionex rechazo la orden para este par.",
    };
  }
  if (exchangeEstado === "SIN_LIQUIDEZ") {
    return {
      tone: "danger",
      texto: "No negociable: mercado detenido en Pionex",
      detalle:
        exchange.motivo ||
        "Pionex devuelve el precio pero con volumen 0: no hay a quien comprar ni vender.",
    };
  }
  if (exchangeEstado === "SIN_DATOS") {
    return {
      tone: "danger",
      texto: "No negociable: Pionex no da datos",
      detalle:
        exchange.motivo || "El exchange no entrega velas de este par, no hay precio ni indicadores.",
    };
  }

  if (jornada.no_compra) {
    return {
      tone: "danger",
      texto: "Mercado cerrado para el bot",
      detalle: jornada.motivo || "El bot esta fuera de su ventana horaria y no opera.",
    };
  }

  if (String(jornada.estado || "").toUpperCase() === "REDUCIDA") {
    return {
      tone: "warning",
      texto: "Negociable (jornada reducida)",
      detalle:
        jornada.motivo || "Fuera de la ventana principal de NY: el bot opera con tamano reducido.",
    };
  }

  return { tone: "ok", texto: "Negociable", detalle: "Exchange operativo y el bot en jornada activa." };
};

const MarketStateBadges = ({ estado, omitKeys = [] }) => {
  const omit = Array.isArray(omitKeys) ? omitKeys : [];
  const items = buildMarketStateBadges(estado).filter((item) => !omit.includes(item.key));
  if (!items.length) return null;
  return (
    <div className="market-state-badges">
      {items.map((item) => (
        <span key={item.key} className={`market-state-badge tone-${item.tone}`} title={item.title}>
          {item.label}
        </span>
      ))}
    </div>
  );
};

/**
 * Resumen de la jornada, para mostrar UNA vez en el encabezado de "Pares en analisis".
 *
 * No es un badge de alerta: informa la ventana horaria del bot, que es igual para todos
 * los pares. Repetirlo en cada tarjeta no dice nada de ningun par en concreto, asi que
 * va en el encabezado y solo dice algo cuando el bot NO esta comprando.
 */
export const JornadaResumen = ({ jornada }) => {
  const estado = String(jornada?.estado || "").toUpperCase();
  if (!estado || estado === "NORMAL") return null;
  const reducido = estado === "REDUCIDA";
  return (
    <span
      className={`market-state-badge jornada-resumen tone-${reducido ? "warning" : "danger"}`}
      title={[jornada?.motivo, jornada?.descripcion].filter(Boolean).join(" · ") || undefined}
    >
      {reducido
        ? "Fuera de jornada NY: el bot sigue comprando con tamaño reducido"
        : "Fuera de jornada NY: el bot no compra hasta la apertura"}
    </span>
  );
};

export default MarketStateBadges;

export { MarketStateBadges };
